import axios from "axios"
import { reactive } from "vue"

const toItem = (plan, { trialId } = {}) => {
  const years = plan.id?.match(/(\d+)_years?/)?.[1]
  return {
    item_id: plan.rawSfId || plan.id,
    item_name: plan.name || plan.id,
    item_brand: "PRTG",
    item_category: "PPHM",
    item_category2: "Software / Subscription / PPHM",
    item_category3: "Subscription",
    item_category4: plan.sensors ? String(plan.sensors) : undefined,
    item_category5: years ? `${years} ${years === "1" ? "year" : "years"}` : undefined,
    item_variant: "create",
    affiliation: "Paessler Hosted Online Store",
    price: (plan.priceInCents || 0) / 100,
    quantity: 1,
    is_trial: false,
    trial_id: trialId,
  }
}

export const pushEcommerceStep = (event, plan, { trialId, ecommerce = {}, ...rest } = {}) => {
  const item = toItem(plan, { trialId })
  const payload = {
    event,
    ecommerce: {
      currency: plan.currencyCode,
      value: item.price * item.quantity,
      ...ecommerce,
      items: [item],
    },
    ...rest,
  }
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ ecommerce: null })
  window.dataLayer.push(payload)
}

const stepNames = {
  1: "instance details",
  2: "plan selection",
  3: "billing address",
  4: "payment details",
  5: "payment confirmation",
}

export const pushCheckoutStepEvent = (step) => {
  const payload = { checkout_step: step, checkout_step_name: stepNames[step] }
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(payload)
}

// Takes useCheckout() rather than importing it: useCheckout imports the store, the store imports
// this file, and the cycle breaks the store. Called once per checkout view, so begin_checkout is
// deduplicated per checkout. A plain object works too. Each function destructures the checkout when
// it runs, so it always sees the current values.
export const useCheckoutTracking = (checkout) => {
  // Unwraps the refs of useCheckout(); a plain object is read through as is.
  const state = reactive(checkout)

  // The plan begin_checkout was last sent for, so going back and forth does not send it twice.
  let checkoutStartedFor = null

  const track = (event, payload = {}, trackedPlan = state.plan) => {
    const { stackId, checkoutId } = state

    if (!trackedPlan) {
      return
    }

    pushEcommerceStep(event, trackedPlan, {
      // The spec wants undefined, not null, when it is not a trial conversion.
      trialId: stackId,
      ...payload,
      ecommerce: {
        subscriptionId: checkoutId,
        ...payload.ecommerce,
      },
    })
  }

  const paymentType = () => {
    const { paymentMethod, selectedCard } = state

    if (paymentMethod === "invoice") {
      return "Invoice"
    }

    if (paymentMethod === "saved_card") {
      return `Saved ${selectedCard?.brand || "Card"}`
    }

    return "Card"
  }

  // Call before the plan changes: the previous plan leaves the cart first.
  const trackPlanChange = (next) => {
    const { plan } = state

    if (plan?.id === next?.id) {
      return
    }

    track("remove_from_cart")
    track("add_to_cart", {}, next)
  }

  const trackBeginCheckout = () => {
    const { plan } = state

    if (checkoutStartedFor === plan?.id) {
      return
    }

    checkoutStartedFor = plan?.id
    track("begin_checkout")
  }

  const trackShippingInfo = () => {
    const { billingAddress } = state
    track("add_shipping_info", { billing_country: billingAddress?.country })
  }

  const trackPaymentInfo = () => track("add_payment_info", { ecommerce: { payment_type: paymentType() } })

  const trackPurchase = () => {
    const { checkoutId, taxInCents } = state
    track("purchase", {
      ecommerce: {
        transaction_id: checkoutId,
        tax: (taxInCents || 0) / 100,
        shipping: 0,
      },
    })
  }

  const trackCancel = () => track("remove_from_cart")

  return { trackPlanChange, trackBeginCheckout, trackShippingInfo, trackPaymentInfo, trackPurchase, trackCancel }
}

// I have by design not await this function later because I don't want to catch possible errors and show
// them as a toast.
export const thirdPartyTrackingApi = config => import.meta.env.VITE_ENABLE_TRACKING === "true" && config.params.sub_id
  ? axios
      .create()
      .get("https://us-central1-big-query-adwords-cockpit.cloudfunctions.net/pairFpsIdSubId", config)
  : () => {}
